import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { api, formatErr } from "../../../../lib/api";
import { Button } from "../../../../components/ui/button";
import { Input } from "../../../../components/ui/input";
import { Textarea } from "../../../../components/ui/textarea";
import { Save, Send } from "lucide-react";
import { toast } from "sonner";
import { Field, SectionCard } from "./shared";
import LineGrid, { blankGridRow, formatCurrency, gridRowsToLines } from "./LineGrid";
import { encodeBankSelection, encodeLedgerSelection, encodeVendorSelection } from "./AccountSelect";
import { ACCOUNTING_BASE } from "./accountingNav";
import VendorBillEditorForm from "./VendorBillEditorForm";
import JournalRows, { journalLineToRow } from "./JournalRows";

const VOUCHER_TYPE_LABELS = {
  RECEIPT: "Receipt", PAYMENT: "Payment", CONTRA: "Contra", JOURNAL: "Journal",
  PURCHASE: "Purchase / Vendor Bill", CREDIT_NOTE: "Credit Note", DEBIT_NOTE: "Debit Note",
  ADJUSTMENT: "Adjustment", OPENING: "Opening",
};

function lineToRow(line) {
  let selection = "";
  if (line.bankAccountId) selection = encodeBankSelection(line.bankAccountId);
  else if (line.vendorId) selection = encodeVendorSelection(line.vendorId);
  else if (line.ledgerId) selection = encodeLedgerSelection(line.ledgerId);
  return {
    key: line.id || Math.random().toString(36).slice(2), selection,
    debit: line.debit > 0 ? line.debit : "", credit: line.credit > 0 ? line.credit : "",
    partyName: line.partyName || "", referenceNo: line.referenceNo || "", narration: line.narration || "",
  };
}

/**
 * Generic edit route (/vouchers/:id/edit) - works for every voucher type,
 * using the same multi-line grid as Journal/Vendor Bill/Credit Note/Debit
 * Note. Restoring an existing voucher for editing is a power-user action
 * regardless of which "operational" screen originally created it
 * (Payment/Receipt/Contra included), so showing the real ledger/bank/vendor
 * selection here - rather than re-deriving which specialized screen it came
 * from - is the same acceptable "advanced view" the Journal screen already
 * uses (see the Journal exception). Every existing line (ledger, bank
 * account, or vendor selection, debit/credit, party, reference, narration)
 * is restored exactly as stored.
 *
 * Correction pass ("Safe Edit"): a POSTED voucher can now be reached here
 * too, not just a draft - the backend (`PUT .../vouchers/:id`) decides
 * whether it is actually safe (no reversal/Billing-source/bank-reco/vendor-
 * balance dependency, period still open) and returns an exact reason if
 * not; this page just surfaces whatever it says rather than blocking
 * client-side. A cancelled voucher is still never reachable here - the
 * backend 409s and this page redirects back to the read-only view.
 */
export default function VoucherEditPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [voucher, setVoucher] = useState(null);
  const [rows, setRows] = useState([blankGridRow(), blankGridRow()]);
  const [date, setDate] = useState("");
  const [referenceNo, setReferenceNo] = useState("");
  const [paymentMode, setPaymentMode] = useState("");
  const [narration, setNarration] = useState("");
  const [manualVoucherNumber, setManualVoucherNumber] = useState("");
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const res = await api.get(`/society/accounting/engine/vouchers/${id}`);
        const row = res.data;
        if (row.status !== "draft" && row.status !== "posted") {
          toast.error("Only a draft or posted voucher can be edited here; a cancelled voucher is permanent history");
          navigate(`${ACCOUNTING_BASE}/vouchers/${id}`, { replace: true });
          return;
        }
        setVoucher(row);
        const linesData = row.lines || [];
        // JOURNAL LEDGER CONTROL correction: a vendorId line is now a normal
        // case the simple editor understands natively (see
        // journalLineToRow) - only a bankAccountId line (still excluded from
        // Journal entirely, per item 4) forces the legacy grid fallback.
        const useSimpleJournalRows = row.voucherType === "JOURNAL" && linesData.every((l) => !l.bankAccountId);
        setRows(linesData.map(useSimpleJournalRows ? journalLineToRow : lineToRow));
        setDate(row.voucherDate);
        setReferenceNo(row.referenceNo || "");
        setPaymentMode(row.paymentMode || "");
        setNarration(row.narration || "");
        setManualVoucherNumber(row.manualVoucherNumber || "");
      } catch (err) {
        toast.error(formatErr(err, "Could not load this voucher"));
        navigate(`${ACCOUNTING_BASE}/vouchers`, { replace: true });
      } finally {
        setLoading(false);
      }
    })();
  }, [id, navigate]);

  const lines = gridRowsToLines(rows);
  const totalDebit = lines.reduce((s, l) => s + l.debit, 0);
  const totalCredit = lines.reduce((s, l) => s + l.credit, 0);
  const balanced = lines.length >= 2 && Math.round((totalDebit - totalCredit) * 100) / 100 === 0 && totalDebit > 0;

  // A Purchase / Vendor Bill's Reference IS the vendor's own invoice number
  // (never an auto-generated society sequence) - required here too, since
  // this generic editor is also reachable for existing Vendor Bill drafts.
  const referenceRequired = voucher?.voucherType === "PURCHASE";
  const isPosted = voucher?.status === "posted";

  const save = async (thenPost) => {
    if (lines.length < 2) { toast.error("A voucher needs at least two lines"); return; }
    if (referenceRequired && !referenceNo.trim()) { toast.error("Vendor Invoice No. is required for a Purchase / Vendor Bill"); return; }
    try {
      setBusy(true);
      await api.put(`/society/accounting/engine/vouchers/${id}`, {
        // paymentNature is carried over from the loaded voucher rather than
        // left to the backend's "other" default - this generic grid editor
        // must never silently reset a Payment's explicit Direct Expense /
        // Vendor-Payable declaration back to an unvalidated "Other" just
        // because it was opened here instead of the Payment Voucher screen.
        voucherType: voucher.voucherType, voucherDate: date, narration, referenceNo, paymentMode,
        paymentNature: voucher.paymentNature || "other", manualVoucherNumber, lines,
      });
      if (isPosted) {
        // Editing a posted voucher takes effect immediately (the backend
        // rebuilds its ledger lines in place) - there is no separate post
        // step here, unlike a draft.
        toast.success("Voucher updated - accounting entries recalculated immediately");
      } else if (thenPost) {
        await api.post(`/society/accounting/engine/vouchers/${id}/post`);
        toast.success("Voucher updated and posted");
      } else {
        toast.success("Draft updated");
      }
      navigate(`${ACCOUNTING_BASE}/vouchers/${id}`);
    } catch (err) {
      toast.error(formatErr(err, "Could not save this voucher"));
    } finally {
      setBusy(false);
    }
  };

  if (loading) return <div className="text-sm text-[#6B7280]">Loading...</div>;
  if (!voucher) return null;

  // Phase 15 CORRECTION (Tally-like Purchase / Vendor Bill), item 12:
  // "Remove the raw journal UI from Purchase Voucher" applies to Edit too,
  // not just Create - so a Vendor Bill reached via this generic editor
  // (e.g. from the Recent Vendor Bills list's Edit action) gets the exact
  // same Vendor + Expense/Asset-allocation UI as creating a new one,
  // instead of the raw Debit/Credit grid below. Every other voucher type
  // (Journal, Credit Note, Debit Note, and the generic Draft Edit path)
  // keeps that grid completely unchanged - this is the ONLY branch added
  // to this shared file.
  // JOURNAL VOUCHER — SIMPLE TALLY-STYLE CORRECTION: editing an existing
  // Journal gets the same compact Dr/Cr row editor as creating a new one
  // (JournalRows.jsx), not the raw multi-column LineGrid below - mirrors the
  // PURCHASE branch immediately below it (the only other branch in this
  // shared file). Every other voucher type reaching this generic editor
  // (Credit Note, Debit Note, the generic Draft path) is completely
  // unaffected. Saving still goes through the exact same PUT
  // .../vouchers/:id the generic path below uses - only the row UI differs.
  // A handful of older Journal vouchers may still carry a Bank/Cash line
  // from before Phase 13 excluded Bank/Cash from Journal entirely - the new
  // simple row editor has no representation for a bankAccountId line (by
  // design, per item 4), so editing one of those old journals here falls
  // through to the generic LineGrid editor below instead, rather than
  // silently dropping that leg of the entry. A vendorId line is NOT
  // legacy - the JOURNAL LEDGER CONTROL correction makes picking a Vendor's
  // own dedicated ledger a normal, fully-supported Journal case (see
  // journalLineToRow), so it stays on this simple editor.
  const isSimpleJournal = voucher.voucherType === "JOURNAL" && (voucher.lines || []).every((l) => !l.bankAccountId);
  if (isSimpleJournal) {
    const journalBalanced = lines.length >= 2 && Math.round((totalDebit - totalCredit) * 100) / 100 === 0 && totalDebit > 0;
    return (
      <div className="space-y-5">
        <div>
          <Link to={`${ACCOUNTING_BASE}/vouchers/${id}`} className="text-xs font-semibold uppercase tracking-[0.16em] text-[#6B7280] hover:text-[#111827]">
            &larr; Back to voucher
          </Link>
          <h2 className="mt-1 text-lg font-extrabold text-[#111827]">Edit Journal {isPosted ? `#${voucher.voucherNumber}` : ""}</h2>
        </div>
        <SectionCard
          title="Journal entry"
          subtitle={isPosted ? "Saving updates this journal in place (same voucher number) and immediately recalculates the General Ledger and Trial Balance - no separate posting step." : undefined}
        >
          <div className="grid gap-3 md:grid-cols-3">
            <Field label="Date"><Input type="date" value={date} onChange={(e) => setDate(e.target.value)} /></Field>
            <Field label="Reference"><Input value={referenceNo} onChange={(e) => setReferenceNo(e.target.value)} placeholder="Optional" /></Field>
            <div className="md:col-span-3">
              <Field label="Narration"><Textarea rows={2} value={narration} onChange={(e) => setNarration(e.target.value)} /></Field>
            </div>
          </div>
          <div className="mt-4">
            <JournalRows rows={rows} onChange={setRows} />
          </div>
          <div className="mt-4 flex flex-wrap items-center gap-3">
            <Button type="button" disabled={busy || !journalBalanced} onClick={() => save(!isPosted)} className="bg-[#111827] text-white hover:bg-[#374151]" title={!journalBalanced ? "Total Dr must equal Total Cr" : undefined}>
              <Save className="mr-2 h-4 w-4" /> Save changes
            </Button>
            {!journalBalanced && lines.length > 0 && <span className="text-xs text-[#6B7280]">Difference: {formatCurrency(Math.abs(totalDebit - totalCredit))}</span>}
          </div>
        </SectionCard>
      </div>
    );
  }

  if (voucher.voucherType === "PURCHASE") {
    return (
      <div className="space-y-5">
        <div>
          <Link to={`${ACCOUNTING_BASE}/vouchers/${id}`} className="text-xs font-semibold uppercase tracking-[0.16em] text-[#6B7280] hover:text-[#111827]">
            &larr; Back to voucher
          </Link>
          <h2 className="mt-1 text-lg font-extrabold text-[#111827]">Edit Vendor Bill {voucher.voucherNumber ? `#${voucher.voucherNumber}` : ""}</h2>
          <p className="mt-1 text-sm text-[#6B7280]">Saving updates this bill's accounting entries in place immediately - no separate posting step.</p>
        </div>
        <VendorBillEditorForm
          mode="edit" voucherId={id} initialVoucher={voucher}
          onSaved={() => navigate(`${ACCOUNTING_BASE}/vouchers/${id}`)}
        />
      </div>
    );
  }

  return (
    <div className="space-y-5">
      <div>
        <Link to={`${ACCOUNTING_BASE}/vouchers/${id}`} className="text-xs font-semibold uppercase tracking-[0.16em] text-[#6B7280] hover:text-[#111827]">
          &larr; Back to voucher
        </Link>
        <h2 className="mt-1 text-lg font-extrabold text-[#111827]">
          Edit {VOUCHER_TYPE_LABELS[voucher.voucherType] || voucher.voucherType} {isPosted ? `#${voucher.voucherNumber}` : "Draft"}
        </h2>
      </div>

      <SectionCard
        title="Voucher entry"
        subtitle={isPosted
          ? "This voucher is already posted. Saving updates it in place (same voucher number) and immediately recalculates the General Ledger, Bank/Cash Book, Trial Balance and Party outstanding - no separate posting step."
          : "Posting is unavailable until Total Debit = Total Credit."}
      >
        <div className="grid gap-3 md:grid-cols-3">
          <Field label="Date"><Input type="date" value={date} onChange={(e) => setDate(e.target.value)} /></Field>
          <Field label={referenceRequired ? "Vendor Invoice No. *" : "Reference"}>
            <Input value={referenceNo} onChange={(e) => setReferenceNo(e.target.value)} required={referenceRequired} />
          </Field>
          <Field label="Payment Mode"><Input value={paymentMode} onChange={(e) => setPaymentMode(e.target.value)} /></Field>
          {!isPosted && voucher.voucherType === "PAYMENT" && (
            <Field label="Manual Voucher Number (optional)">
              <Input value={manualVoucherNumber} onChange={(e) => setManualVoucherNumber(e.target.value.toUpperCase())} placeholder="Leave blank for automatic number" />
            </Field>
          )}
          <div className="md:col-span-3">
            <Field label="Narration"><Textarea rows={2} value={narration} onChange={(e) => setNarration(e.target.value)} /></Field>
          </div>
        </div>

        <div className="mt-4">
          <LineGrid rows={rows} onChange={setRows} />
        </div>

        <div className="mt-4 flex flex-wrap gap-2">
          {isPosted ? (
            <Button type="button" disabled={busy || !balanced} onClick={() => save(false)} className="bg-[#111827] text-white hover:bg-[#374151]" title={!balanced ? "Total Debit must equal Total Credit" : undefined}>
              <Save className="mr-2 h-4 w-4" /> Save changes
            </Button>
          ) : (
            <>
              <Button type="button" variant="outline" disabled={busy} onClick={() => save(false)}><Save className="mr-2 h-4 w-4" /> Save Draft</Button>
              <Button type="button" disabled={busy || !balanced} onClick={() => save(true)} className="bg-[#111827] text-white hover:bg-[#374151]" title={!balanced ? "Total Debit must equal Total Credit to post" : undefined}>
                <Send className="mr-2 h-4 w-4" /> Save &amp; Post
              </Button>
            </>
          )}
          {!balanced && lines.length > 0 && <span className="self-center text-xs text-[#6B7280]">Difference: {formatCurrency(Math.abs(totalDebit - totalCredit))}</span>}
        </div>
      </SectionCard>
    </div>
  );
}
